import createClient, { type Middleware } from 'openapi-fetch'
import type { paths } from './generated/schema'

export interface ApiClientOptions {
  baseUrl: string
  /**
   * Returns the current access token. A getter rather than a value because the
   * token lives in Zustand memory and rotates on refresh (SEC-FR-001) — capturing
   * it once would pin a stale token for the session.
   */
  getAccessToken: () => string | null
  /** Attempts a refresh; resolves to the new token or null when the session is over. */
  refresh?: () => Promise<string | null>
  /** Called when refresh fails, so the app can clear state and route to login. */
  onUnauthorized?: () => void
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
    /** Correlation id from the server, surfaced in ErrorState for support. */
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * F0-48 — the typed fetch client.
 *
 * `credentials: 'include'` is required: the refresh token is an HttpOnly cookie
 * set by the backend (SEC-FR-001), so the refresh call must carry it while the
 * access token travels in the Authorization header from memory.
 *
 * The 401 path refreshes once and replays the original request. Concurrent 401s
 * share a single in-flight refresh — without that, a dashboard firing six
 * parallel queries on a stale token would trigger six refreshes and, with
 * rotating refresh tokens, invalidate its own session.
 */
export function createApiClient({
  baseUrl,
  getAccessToken,
  refresh,
  onUnauthorized,
}: ApiClientOptions) {
  let inFlightRefresh: Promise<string | null> | null = null

  function refreshOnce() {
    if (!refresh) return Promise.resolve(null)
    inFlightRefresh ??= refresh().finally(() => {
      inFlightRefresh = null
    })
    return inFlightRefresh
  }

  const authMiddleware: Middleware = {
    async onRequest({ request }) {
      const token = getAccessToken()
      if (token) request.headers.set('Authorization', `Bearer ${token}`)
      request.headers.set('Accept', 'application/json')
      return request
    },

    async onResponse({ request, response }) {
      if (response.status !== 401) return response

      const token = await refreshOnce()
      if (!token) {
        onUnauthorized?.()
        return response
      }

      // Replay once with the fresh token.
      const retry = new Request(request.url, {
        method: request.method,
        headers: new Headers(request.headers),
        body: request.bodyUsed ? undefined : request.body,
        credentials: 'include',
      })
      retry.headers.set('Authorization', `Bearer ${token}`)
      return fetch(retry)
    },
  }

  const client = createClient<paths>({
    baseUrl,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
  client.use(authMiddleware)
  return client
}

export type ApiClient = ReturnType<typeof createApiClient>

/**
 * Normalises an openapi-fetch result into a value or a thrown ApiError, so query
 * functions read as `return unwrap(await client.GET(...))`.
 */
export function unwrap<T>(result: {
  data?: T
  error?: unknown
  response: Response
}): T {
  if (result.error !== undefined || !result.response.ok) {
    const detail = result.error as { detail?: unknown } | undefined
    throw new ApiError(
      result.response.status,
      describeStatus(result.response.status),
      detail?.detail ?? result.error,
      result.response.headers.get('x-request-id') ?? undefined,
    )
  }
  return result.data as T
}

/** User-facing wording. Never surfaces a raw status code as the primary message. */
function describeStatus(status: number) {
  switch (status) {
    case 400:
      return 'The request was not valid.'
    case 401:
      return 'Your session has expired. Sign in again to continue.'
    case 403:
      // AC-53: the server is the authorisation boundary; the UI explains, not guesses.
      return 'You do not have permission to do this in this workspace.'
    case 404:
      return 'That item no longer exists, or you do not have access to it.'
    case 409:
      return 'Someone else changed this first. Reload to see the current version.'
    case 422:
      return 'Some fields need attention.'
    case 429:
      return 'Too many requests. Wait a moment and try again.'
    default:
      return status >= 500
        ? 'Something went wrong on our side. The team has been notified.'
        : 'The request could not be completed.'
  }
}
